import React, { useContext, useEffect, useRef, useState } from "react";
import { FaFileExcel } from "react-icons/fa6";
import { CgAttachment } from "react-icons/cg";
import BtnSubmit from "../Buttons/BtnSubmit/BtnSubmit";
import { GET, POSTFORMDATA } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import Toast from "../Toast/Toast";
import { useNavigate, useParams } from "react-router-dom";
import FullScreenLoader from "../FullScreenLoader/FullScreenLoader";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";
import BulkUploadErrorPanel from "./BulkUploadErrorPanel";
import { parseBulkIssuanceError } from "./utils/parseBulkIssuanceError";

export const FileUpload = ({
  transaction_name,
  isPostPaid,
  testId = "file-upload",
}) => {
  const dropzoneInputId = `${testId}-dropzone-file`;
  const { credential_template_id, issuer_credential_template_id } = useParams();
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [bulkUploadError, setBulkUploadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [isReadyToSubmit, setIsReadyToSubmit] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const [isDragging, setIsDragging] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(0);

  const userDetails = () => {
    GET(EntityConstants.CURRENT_USER)
      .then((response) => response.json())
      .then((data) => {
        setAvailableCredits(data.available_credits);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  useEffect(() => {
    userDetails();

    GET(EntityConstants.GET_ORG_DETAILS)
      .then((response) => response.json())

      .catch((error) => {
        console.error("Error fetching organisation details:", error);
      });
  }, []);

  useEffect(() => {
    userDetails();
  }, []);

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    if (event.dataTransfer.files.length > 0) {
      validateFile(event.dataTransfer.files[0]);
    }
  };

  const MAX_FILE_SIZE_KB = 100;

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      validateFile(file);
    }
  };

  const handleDropzoneKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      document.getElementById(dropzoneInputId)?.click();
    }
  };

  const clearBulkUploadError = () => setBulkUploadError(null);

  const validateFile = (file) => {
    // 10KB -MAX_FILE_SIZE_KB

    const fileSizeKiloBytes = file.size / 1024;
    clearBulkUploadError();

    if (fileSizeKiloBytes > MAX_FILE_SIZE_KB) {
      setErrorMsg(`${t("designs:file_exceed_limit")} (${MAX_FILE_SIZE_KB}KB)`);
      return;
    }
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (["xlsx", "xls"].includes(fileExtension)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets["data"];
        if (!worksheet) {
          setErrorMsg(t("designs:missing_data_sheet"));
          return;
        }
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const rowCount = jsonData
          .slice(1)
          .filter((row) =>
            row.some((cell) => cell !== undefined && cell !== ""),
          ).length;
        authContext.setFileUploaded(true);
        authContext.setRowCount(rowCount);
        setSelectedFile(file);
        if (isPostPaid || rowCount <= availableCredits) {
          setIsReadyToSubmit(true);
          setErrorMsg("");
          authContext.setCreditCount(rowCount);
        } else {
          setIsReadyToSubmit(false);
          setErrorMsg(t("designs:insufficient_credits"));
          authContext.setCreditCount("Insufficient Credits");
          Toast({
            type: "warning",
            title: t("designs:insufficient_credits"),
            message: t("designs:not_enough_credits"),
          });
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (fileExtension === "csv") {
      const reader = new FileReader();
      reader.onload = (e) => {
        const csvData = e.target.result;
        Papa.parse(csvData, {
          complete: (results) => {
            const rowCount = results.data
              .slice(1)
              .filter((row) =>
                row.some((cell) => cell !== undefined && cell !== ""),
              ).length;
            authContext.setFileUploaded(true);
            authContext.setRowCount(rowCount);
            setSelectedFile(file);

            if (isPostPaid || rowCount <= availableCredits) {
              setIsReadyToSubmit(true);
              setErrorMsg("");
              authContext.setCreditCount(rowCount);
            } else {
              setIsReadyToSubmit(false);
              setErrorMsg(t("designs:insufficient_credits"));
              authContext.setCreditCount("Insufficient Credits");
              Toast({
                type: "warning",
                title: t("designs:insufficient_credits"),
                message: t("designs:not_enough_credits"),
              });
            }
          },
        });
      };
      reader.readAsText(file);
    } else {
      setErrorMsg(t("designs:unsupported_file_format"));
      return;
    }
    setSelectedFile(file);
    setErrorMsg("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmittingRef.current) return;

    let didNavigate = false;
    try {
      if (!selectedFile) {
        setErrorMsg(t("designs:choose_file"));
        return;
      }

      if (!isReadyToSubmit) {
        setErrorMsg(t("designs:insufficient_credits"));
        Toast({
          type: "warning",
          title: t("designs:insufficient_credits"),
          message: t("designs:not_enough_credits"),
        });

        if (authContext.rowCount > availableCredits) {
          authContext.setCreditCount("Insufficient Credits");
        }
        return;
      }

      isSubmittingRef.current = true;
      setIsSubmitting(true);
      const form_data = new FormData();
      form_data.append("file", selectedFile);

      const apiUrl = `${EntityConstants.POST_CREDENTIALS_BULK_ISSUANCE}${issuer_credential_template_id}&mode_of_issuance=issue`;

      const response = await POSTFORMDATA(apiUrl, form_data);
      const data = await response.json();

      if (response.ok) {
        clearBulkUploadError();
        didNavigate = true;
        navigate(
          `/issuer/workspace/batches/summary/${data.credential_template_id}/${data.issuer_credential_template_id}?templateType=${data.template_type}`,
          {
            state: {
              transaction_id: data.transaction_id,
              programme: data.programme_name,
              batch: data.batch_name,
            },
          },
        );
      } else {
        const parsedError = parseBulkIssuanceError(data);
        setBulkUploadError(parsedError);

        Toast({
          type: "error",
          title: t("designs:error"),
          message: data?.error,
        });
      }
    } catch (error) {
      clearBulkUploadError();
      Toast({
        type: "error",
        title: t("designs:error"),
        message: error.message,
      });
    } finally {
      if (!didNavigate) {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    }
  };

  const downloadXLXS = async (template_id) => {
    GET(
      `${EntityConstants.GET_EMAIL_WHATSAPP_DEFAULT_DETAILS}${template_id}/subject?type=xlsx`,
    )
      .then((data) => data.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Sample_${template_id}.xlsx`;
        document.body.appendChild(link);

        // Simulate user click event
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <form
      id={testId}
      className=""
      onSubmit={(event) => handleSubmit(event, transaction_name)}
      onDragEnter={(e) => handleDragEnter(e)}
      onDragOver={(e) => handleDragOver(e)}
      onDragLeave={() => handleDragLeave()}
      onDrop={(e) => handleDrop(e)}
    >
      <div>
        <ul title={t("designs:downloads")} className="px-1 mb-2">
          <li title={t("designs:sample_xlsx_tooltip")}>
            <button
              type="button"
              id={`${testId}-download-sample`}
              onClick={() => downloadXLXS(credential_template_id)}
              className="flex items-center justify-end gap-2 text-purple-700 hover:text-purple-500 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              {t("designs:download_sample_excel")} <FaFileExcel />
            </button>
          </li>
        </ul>
      </div>
      <div className="flex flex-col">
        <div
          id={`${testId}-dropzone`}
          role="button"
          tabIndex={0}
          aria-describedby={errorMsg ? `${testId}-error` : undefined}
          className={`flex flex-col items-center justify-center h-64 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 mb-3.5 ${
            isDragging
              ? "border-blue-500"
              : bulkUploadError
                ? "border-cfErrorText/50 bg-[color-mix(in_srgb,var(--cf-error-bg-color)_55%,white)]"
                : "border-gray-300"
          }`}
          onDragEnter={(e) => handleDragEnter(e)}
          onDragOver={(e) => handleDragOver(e)}
          onDragLeave={() => handleDragLeave()}
          onDrop={(e) => handleDrop(e)}
          onKeyDown={handleDropzoneKeyDown}
          onClick={() => document.getElementById(dropzoneInputId)?.click()}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <svg
              className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 16"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
              />
            </svg>
            <p className="mb-2 text-sm text-gray-500">
              {t("designs:drag_drop")}{" "}
              <span className="font-semibold text-cfPrimaryDarkBlue">
                {t("designs:browse")}
              </span>
            </p>
            <p className="text-xs text-gray-500">
              {t("designs:csv_excel_file")} ({t("designs:max")}{" "}
              {MAX_FILE_SIZE_KB}
              {t("designs:kb")})
            </p>
            {selectedFile && (
              <p className="text-emerald-600 ">{selectedFile.name}</p>
            )}
            <p
              id={`${testId}-error`}
              className="text-red-500"
              role="alert"
              aria-live="polite"
            >
              {errorMsg}
            </p>
          </div>
          <input
            id={dropzoneInputId}
            type="file"
            className="hidden"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={handleFileChange}
          />
        </div>

        <BulkUploadErrorPanel
          errorDetails={bulkUploadError}
          onDismiss={clearBulkUploadError}
          testId={`${testId}-bulk-error`}
        />

        <BtnSubmit
          testId={`${testId}-submit`}
          Display_Icon={CgAttachment}
          DisplayText={t("designs:upload_and_issue")}
          disabled={isSubmitting}
        />
      </div>
      {isSubmitting && <FullScreenLoader />}
    </form>
  );
};
